import React from 'react';
import { AlertTriangle, Copy, Trash2, X } from 'lucide-react';
import { CityPicker } from './CityPicker';
import { FacilitatorPicker } from './FacilitatorPicker';
import { SessionLanguagePicker } from './SessionLanguagePicker';
import { SessionTimezonePicker } from './SessionTimezonePicker';
import type { ProgramSlotDraft } from './ProgramSessionsCalendar';

type Props = {
  slot: ProgramSlotDraft;
  index: number;
  sessionCode?: string | null;
  defaultSlotLabel: (index: number) => string;
  canRemove: boolean;
  /** Vrai si la séance diffère du modèle du programme. */
  isException?: boolean;
  onChange: (patch: Partial<ProgramSlotDraft>) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onClose: () => void;
};

const fieldClass =
  'w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-3.5 text-base font-semibold text-slate-900 transition-colors focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-500/10';

export const ProgramSlotEditor: React.FC<Props> = ({
  slot,
  index,
  sessionCode,
  defaultSlotLabel,
  canRemove,
  isException = false,
  onChange,
  onDuplicate,
  onRemove,
  onClose,
}) => (
  <div className="relative flex min-h-[520px] flex-col overflow-hidden rounded-3xl border border-indigo-200 bg-white shadow-lg shadow-indigo-500/10">
    <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-indigo-500 to-violet-500" />

    <div className="flex items-start justify-between gap-4 border-b border-indigo-100 bg-indigo-50/30 px-6 py-5 pl-7">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Séance {index + 1}</p>
        <h3 className="mt-1 truncate text-xl font-black text-slate-900">
          {slot.label.trim() || defaultSlotLabel(index)}
        </h3>
        {sessionCode && (
          <span className="mt-3 inline-block rounded-lg bg-indigo-600 px-3 py-1.5 font-mono text-sm font-bold text-white">
            {sessionCode}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-xl border border-slate-200 bg-white p-2 text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-700"
        aria-label="Fermer l'éditeur"
      >
        <X className="h-5 w-5" />
      </button>
    </div>

    <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 pl-7">
      {isException && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs font-semibold text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Cette séance diffère du modèle du programme.
        </div>
      )}

      <div>
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Nom</label>
        <input
          value={slot.label}
          onChange={(e) => onChange({ label: e.target.value })}
          className={fieldClass}
          placeholder={defaultSlotLabel(index)}
        />
      </div>

      <div>
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Date et heure</label>
        <input
          type="datetime-local"
          value={slot.scheduledAt}
          onChange={(e) => onChange({ scheduledAt: e.target.value })}
          className={fieldClass}
        />
      </div>

      <SessionLanguagePicker compact label="Langue" value={slot.language} onChange={(language) => onChange({ language })} />

      <CityPicker
        compact
        label="Ville / Lieu"
        value={slot.location}
        onChange={(location, timezone) => {
          // Choisir une ville connue présélectionne immédiatement son fuseau.
          onChange(timezone ? { location, timezone } : { location });
        }}
      />

      <FacilitatorPicker
        compact
        label="Animateur"
        value={slot.facilitator}
        onChange={(facilitator) => onChange({ facilitator })}
      />

      <SessionTimezonePicker
        compact
        label="Fuseau horaire"
        value={slot.timezone}
        onChange={(timezone) => onChange({ timezone })}
      />
    </div>

    <div className="flex gap-3 border-t border-indigo-100 bg-slate-50/50 px-6 py-4 pl-7">
      <button
        type="button"
        onClick={onDuplicate}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-indigo-200 bg-white px-4 py-3 text-sm font-bold text-indigo-700 transition-colors hover:bg-indigo-50"
      >
        <Copy className="h-4 w-4" />
        Dupliquer
      </button>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-red-200 bg-white px-4 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
          Retirer
        </button>
      )}
    </div>
  </div>
);

export const ProgramSlotEditorPlaceholder: React.FC = () => (
  <div className="flex min-h-[520px] flex-col items-center justify-center rounded-3xl border border-dashed border-indigo-200 bg-indigo-50/20 px-8 py-12 text-center">
    <p className="text-base font-bold text-slate-700">Aucune séance sélectionnée</p>
    <p className="mt-2 max-w-[240px] text-sm leading-relaxed text-slate-500">
      Cliquez une séance dans le calendrier ou la liste pour l'éditer ici.
    </p>
  </div>
);
